import { z } from 'zod';

// =============================================================================
//  SuspensionConfig schemas — Story 12.2 (Fase 4 / E12)
//  FR-52.1..52.5 · DR-022 (opt-in seguro) · DR-028 (audit + roles) · DR-041
//
//  Política global de suspensão automática por inadimplência. Tabela singleton
//  (id=1). Default seguro: suspension_enabled=false → nada suspende sozinho.
//
//  Validações de negócio (AC8):
//   - suspension_days_after_due: 1..365 (D+N do vencimento)
//   - suspension_grace_hours: 0..72 (carência extra em horas)
//   - risk_warning_days_before: 1..(suspension_days_after_due - 1)
//     (o aviso de risco PRECISA ocorrer ANTES da suspensão)
// =============================================================================

export const SUSPENSION_DAYS_MIN = 1;
export const SUSPENSION_DAYS_MAX = 365;
export const SUSPENSION_GRACE_HOURS_MIN = 0;
export const SUSPENSION_GRACE_HOURS_MAX = 72;
export const RISK_WARNING_DAYS_MIN = 1;

/**
 * Response — configuração completa exposta no GET (AC2/AC7).
 * `updated_at` em ISO; `updated_by_user_id` pode ser null (nunca editada ainda).
 */
export const suspensionConfigSchema = z.object({
  suspension_enabled: z.boolean(),
  suspension_days_after_due: z.number().int(),
  suspension_grace_hours: z.number().int(),
  risk_warning_days_before: z.number().int(),
  updated_at: z.string(),
  updated_by_user_id: z.string().uuid().nullable(),
  version: z.number().int(),
});
export type SuspensionConfigDto = z.infer<typeof suspensionConfigSchema>;

export const suspensionConfigResponseSchema = z.object({
  data: suspensionConfigSchema,
});
export type SuspensionConfigResponse = z.infer<typeof suspensionConfigResponseSchema>;

/**
 * Body do PATCH (AC8). Campos de política são opcionais (patch parcial), mas
 * `version` é obrigatório (optimistic lock). O cross-field
 * `risk_warning_days_before < suspension_days_after_due` é validado no
 * superRefine usando os valores efetivos (body OU defaults conhecidos não estão
 * disponíveis aqui — o service revalida contra a row atual; o Zod cobre o caso
 * em que AMBOS vêm no mesmo patch).
 */
export const updateSuspensionConfigSchema = z
  .object({
    suspension_enabled: z.boolean().optional(),
    suspension_days_after_due: z
      .number()
      .int('Dias após vencimento deve ser inteiro.')
      .min(SUSPENSION_DAYS_MIN, `Mínimo ${SUSPENSION_DAYS_MIN} dia.`)
      .max(SUSPENSION_DAYS_MAX, `Máximo ${SUSPENSION_DAYS_MAX} dias.`)
      .optional(),
    suspension_grace_hours: z
      .number()
      .int('Horas de carência deve ser inteiro.')
      .min(SUSPENSION_GRACE_HOURS_MIN, 'Carência não pode ser negativa.')
      .max(SUSPENSION_GRACE_HOURS_MAX, `Máximo ${SUSPENSION_GRACE_HOURS_MAX} horas.`)
      .optional(),
    risk_warning_days_before: z
      .number()
      .int('Dias de aviso deve ser inteiro.')
      .min(RISK_WARNING_DAYS_MIN, `Mínimo ${RISK_WARNING_DAYS_MIN} dia.`)
      .optional(),
    version: z.number().int('Versão deve ser inteiro.').min(1, 'Versão obrigatória.'),
  })
  .strict()
  .superRefine((value, ctx) => {
    // Cross-field só checável aqui quando AMBOS vêm no patch. Quando só um vem,
    // o service compara contra o valor persistido (a row atual).
    if (
      value.risk_warning_days_before !== undefined &&
      value.suspension_days_after_due !== undefined &&
      value.risk_warning_days_before >= value.suspension_days_after_due
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['risk_warning_days_before'],
        message: 'Aviso de risco deve ocorrer antes da suspensão (menor que dias após vencimento).',
      });
    }
  });
export type UpdateSuspensionConfigDto = z.infer<typeof updateSuspensionConfigSchema>;

/**
 * Body do toggle de opt-out por cliente (AC9).
 */
export const clientSuspensionOptOutSchema = z
  .object({
    skip_auto_suspension: z.boolean(),
  })
  .strict();
export type ClientSuspensionOptOutDto = z.infer<typeof clientSuspensionOptOutSchema>;

export const clientSuspensionOptOutResponseSchema = z.object({
  data: z.object({
    client_id: z.string().uuid(),
    skip_auto_suspension: z.boolean(),
  }),
});
export type ClientSuspensionOptOutResponse = z.infer<typeof clientSuspensionOptOutResponseSchema>;

/**
 * Body do toggle de suspensão automática por SaaS (AC10).
 */
export const saasAutoSuspensionSchema = z
  .object({
    auto_suspension_enabled: z.boolean(),
  })
  .strict();
export type SaasAutoSuspensionDto = z.infer<typeof saasAutoSuspensionSchema>;

export const saasAutoSuspensionResponseSchema = z.object({
  data: z.object({
    saas_product_id: z.string().uuid(),
    auto_suspension_enabled: z.boolean(),
  }),
  // Aviso explícito quando liga em SaaS sem integration_level='full' (AC10).
  warning: z.string().optional(),
});
export type SaasAutoSuspensionResponse = z.infer<typeof saasAutoSuspensionResponseSchema>;
